import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Search, X, ClipboardList, FileText, Users, Building2, User } from 'lucide-react';
import { cn } from '../lib/utils';

const PHASE_LABELS = ['','Erfassung','Terminierung','Rapportieren','Verrechnen','Abgeschlossen'];

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    if (!open) { setQuery(''); setResults(null); }
  }, [open]);

  useEffect(() => {
    if (query.length < 2) { setResults(null); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(query)}`);
        setResults(res.data);
      } catch {} finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const go = (path) => { navigate(path); setOpen(false); };
  const total = results ? (results.orders.length + results.quotes.length + results.customers.length + (results.owners?.length || 0) + results.properties.length) : 0;

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-btn"
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground bg-muted hover:bg-accent border border-border/60 rounded-sm transition-colors w-full max-w-xs"
      >
        <Search className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
        <span className="flex-1 text-left text-xs">Suchen...</span>
        <kbd className="text-xs bg-background border border-border rounded px-1 hidden sm:block">⌘K</kbd>
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-xl bg-card border border-border shadow-2xl rounded-sm overflow-hidden">
            {/* Search Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
              <Search className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Aufträge, Offerten, Verwaltungen, Eigentümer suchen..."
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                data-testid="search-input"
              />
              {query && (
                <button onClick={() => setQuery('')} className="text-muted-foreground hover:text-foreground transition-colors">
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {results && !loading && total === 0 && (
                <div className="py-8 text-center text-sm text-muted-foreground">Keine Ergebnisse für „{query}"</div>
              )}

              {results && !loading && total > 0 && (
                <div className="py-2">
                  {results.orders.length > 0 && (
                    <div>
                      <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <ClipboardList className="w-3.5 h-3.5" strokeWidth={1.5} /> Aufträge
                      </p>
                      {results.orders.map(o => (
                        <button key={o.id} onClick={() => go(`/orders/${o.id}`)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-accent transition-colors text-left" data-testid={`search-order-${o.id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{o.title}</p>
                            <p className="text-xs text-muted-foreground">{o.order_number} · {o.customer_name || '–'}</p>
                          </div>
                          <span className={cn("text-xs px-2 py-0.5 rounded-sm ml-3 shrink-0", `phase-${o.phase}`)}>
                            {PHASE_LABELS[o.phase]}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {results.quotes.length > 0 && (
                    <div>
                      <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Offerten
                      </p>
                      {results.quotes.map(q => (
                        <button key={q.id} onClick={() => go(`/quotes/${q.id}`)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-accent transition-colors text-left" data-testid={`search-quote-${q.id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{q.title}</p>
                            <p className="text-xs text-muted-foreground">{q.quote_number} · {q.customer_name || '–'}</p>
                          </div>
                          <span className={cn("text-xs px-2 py-0.5 rounded-sm ml-3 shrink-0 capitalize", q.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' : q.status === 'rejected' ? 'bg-red-100 text-red-700' : 'phase-1')}>
                            {q.status === 'accepted' ? 'Angenommen' : q.status === 'rejected' ? 'Abgelehnt' : 'Offen'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {results.customers.length > 0 && (
                    <div>
                      <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Users className="w-3.5 h-3.5" strokeWidth={1.5} /> Verwaltungen
                      </p>
                      {results.customers.map(c => (
                        <button key={c.id} onClick={() => go(`/customers`)} className="w-full flex items-start px-4 py-2.5 hover:bg-accent transition-colors text-left" data-testid={`search-customer-${c.id}`}>
                          <div>
                            <p className="text-sm font-medium text-foreground">{c.name}</p>
                            <p className="text-xs text-muted-foreground">{c.email || c.address || '–'}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {(results.owners?.length || 0) > 0 && (
                    <div>
                      <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <User className="w-3.5 h-3.5" strokeWidth={1.5} /> Eigentümer
                      </p>
                      {results.owners.map(o => (
                        <button key={o.id} onClick={() => go(`/owners`)} className="w-full flex items-start px-4 py-2.5 hover:bg-accent transition-colors text-left" data-testid={`search-owner-${o.id}`}>
                          <div>
                            <p className="text-sm font-medium text-foreground">{o.name}</p>
                            <p className="text-xs text-muted-foreground">{o.email || o.address || '–'}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {results.properties.length > 0 && (
                    <div>
                      <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Liegenschaften
                      </p>
                      {results.properties.map(p => (
                        <button key={p.id} onClick={() => go(`/properties`)} className="w-full flex items-start px-4 py-2.5 hover:bg-accent transition-colors text-left">
                          <p className="text-sm font-medium text-foreground">{p.address}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!query && (
                <div className="px-4 py-6 text-center text-xs text-muted-foreground">
                  Tippen Sie um zu suchen · <kbd className="bg-muted px-1 rounded">Esc</kbd> zum Schliessen
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
